import { useState, useMemo } from "react";
import { Icon } from "../icons";
import { EXERCISE_CATALOGUE, MUSCLE_GROUPS } from "../constants";
import { uid, normalize } from "../helpers";

export default function CataloguePickerModal({ exercises, onAdd, onClose }) {
  const [search, setSearch]           = useState("");
  const [muscleFilter, setMuscleFilter] = useState("all");

  const ownedNames = useMemo(
    () => new Set(exercises.map(e => e.name.toLowerCase())),
    [exercises]
  );

  const filtered = useMemo(() => {
    const q = normalize(search.trim());
    return EXERCISE_CATALOGUE.filter(e => {
      const matchSearch = !q || normalize(e.name).includes(q);
      const matchMuscle = muscleFilter === "all" || e.muscles.includes(muscleFilter);
      return matchSearch && matchMuscle;
    });
  }, [search, muscleFilter]);

  const handleAdd = (catEntry) => {
    onAdd({
      id: uid(),
      name: catEntry.name,
      muscle: catEntry.muscles[0] || "Full Body",
      muscles: catEntry.muscles || [],
      category: catEntry.category || "",
      sets: [],
      oneRepMax: null,
      notes: "",
    });
  };

  const CATEGORY_BADGE = {
    powerlifting:  "badge-accent",
    weightlifting: "badge-teal",
    bodybuilding:  "badge-slate",
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-md" style={{ display: "flex", flexDirection: "column", maxHeight: "85vh" }}>
        <div className="modal-header">
          <div className="modal-title">Add from Catalogue</div>
          <button className="modal-close" onClick={onClose}><Icon.X /></button>
        </div>

        {/* Search */}
        <div style={{ padding: "0 20px 12px" }}>
          <input
            className="input"
            autoFocus
            placeholder="Search exercises…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Muscle filter chips */}
        <div style={{
          display: "flex", gap: 6, overflowX: "auto", padding: "0 20px 12px",
          scrollbarWidth: "none", flexShrink: 0,
        }}>
          {["all", ...MUSCLE_GROUPS.filter(m => m !== "Full Body")].map(m => (
            <button key={m} onClick={() => setMuscleFilter(m)} style={{
              whiteSpace: "nowrap", padding: "5px 12px", borderRadius: 20, fontSize: 11,
              fontWeight: 700, cursor: "pointer", flexShrink: 0,
              background: muscleFilter === m ? "var(--accent)" : "var(--bg3)",
              color: muscleFilter === m ? "#111" : "var(--muted)",
              border: `1px solid ${muscleFilter === m ? "var(--accent)" : "var(--border)"}`,
            }}>{m === "all" ? "All" : m}</button>
          ))}
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 20px 8px" }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 0", color: "var(--muted)", fontSize: 13 }}>
              No matches
            </div>
          )}
          {filtered.map(e => {
            const owned = ownedNames.has(e.name.toLowerCase());
            return (
              <div key={e.name} onClick={() => !owned && handleAdd(e)} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 12px", borderRadius: 8, marginBottom: 4,
                background: "var(--bg3)",
                border: "1px solid var(--border)",
                cursor: owned ? "default" : "pointer",
                opacity: owned ? 0.45 : 1,
                transition: "background 0.1s",
              }}
                onMouseEnter={e => { if (!owned) e.currentTarget.style.background = "var(--bg2)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--bg3)"; }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{e.name}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                    {e.muscles.slice(0, 3).join(" · ")}
                  </div>
                </div>
                {owned
                  ? <span style={{ fontSize: 10, color: "var(--muted)", flexShrink: 0 }}>Added</span>
                  : <span className={`badge ${CATEGORY_BADGE[e.category] || "badge-slate"}`}>{e.category}</span>
                }
              </div>
            );
          })}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
